/* eslint-disable no-useless-escape */
/* eslint-disable max-len */
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { useWindowSize } from '@brands/hooks';
import { verifyConfirmationCode } from '@brands/services/auth/verifyConfirmationCode';
import { yupResolver } from '@hookform/resolvers/yup';
import { MuiOtpInput } from 'mui-one-time-password-input';
import React, { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Modal from 'react-modal';
import * as Yup from 'yup';

import Button from '../../Components/Button/Button';
import { customStylesForgotPassword } from '../../Utils/customStyles';
import { displayErrorDetails } from '../../Utils/displayError';
import ContactCustomerServices from './Components/ContactCustomerServices';
import styles from './styles.module.scss';

interface UpdateEmailModalStepTwoProps {
  updateEmailStep: number;
  setUpdateEmailStep: (num: number) => void;
  credentials: UpdateEmailCredentials;
  setCredentials: (credentials: UpdateEmailCredentials) => void;
}

type UpdateEmailCredentials = {
  username?: string;
  code: string;
  new_password: string;
  reset_password_hash?: string;
  last_four_digits?: string;
};

type UpdateEmailModalStepTwoSubmitForm = {
  verification_code?: string;
};

const validationSchema = Yup.object().shape({
  verification_code: Yup.string()
    .required('Verification code is required')
    .matches(/^\d{6}$/, 'Verification code must be a 6-digit number'),
});

const UpdateEmailModalStepTwo: FC<UpdateEmailModalStepTwoProps> = ({
  updateEmailStep,
  setUpdateEmailStep,
  credentials,
  setCredentials,
}) => {
  const [loading, setLoading] = useState(false);
  const isMobile = useWindowSize().width < 992;
  const [contactCustomerSupport, setContactCustomerSupport] = useState(false);

  const { handleSubmit, setValue, control, watch } = useForm<UpdateEmailModalStepTwoSubmitForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const closeModal = (): void => {
    setUpdateEmailStep(0);
    setCredentials({
      username: '',
      code: '',
      new_password: '',
      reset_password_hash: '',
      last_four_digits: '',
    });
  };

  const onSubmit = async (data: UpdateEmailModalStepTwoSubmitForm): Promise<void> => {
    try {
      setLoading(true);

      const verificationCode = data.verification_code as string;
      await verifyConfirmationCode({
        email: credentials.username?.toLowerCase() || '',
        confirmation_code: verificationCode,
        is_email_update: true,
      }).then(() => {
        setUpdateEmailStep(3);
        setCredentials({
          username: credentials.username,
          code: verificationCode,
          new_password: '',
          reset_password_hash: '',
        });
      });
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (updateEmailStep === 2) {
      document.body.classList.add('modal-open');
    } else if (document.body.classList.contains('modal-open') && updateEmailStep === 0) {
      document.body.classList.remove('modal-open');
    }
  }, [updateEmailStep]);

  return (
    <div className={styles.container}>
      <Sidenav isVisibleOnMobile hideDefaultOptions authLoog hideModals={() => closeModal()} />
      <Modal
        isOpen={updateEmailStep === 2}
        onRequestClose={closeModal}
        style={customStylesForgotPassword(isMobile, '40%')}
        contentLabel="Modal"
        ariaHideApp={false}
        className={`${styles.wrapper} ${styles.forgotPasswordModal}`}
        shouldCloseOnOverlayClick={false}
      >
        {contactCustomerSupport ? (
          <ContactCustomerServices />
        ) : (
          <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
            <span className={`${styles.formTitle} p-b-26`}>
              <span className={`${styles.formTitleForgot} fs-unmask`}>Update Email Address</span>
              <div className={`text-center ${styles.formTitleLink} p-t-115 fs-unmask`}>
                <span className={`${styles.txt1} txt1`}>
                  {`Please enter the verification code sent to the phone number ending in ${credentials.last_four_digits}`}
                </span>
              </div>
            </span>
            <div className={styles.inputWrapper} data-validate="Verification code is required">
              <Controller
                name="verification_code"
                control={control}
                defaultValue=""
                render={({}) => (
                  <MuiOtpInput
                    className="fs-exclude"
                    autoFocus
                    value={watch('verification_code')}
                    onChange={(value) => {
                      setValue('verification_code', value);
                      if (value.length === 6) {
                        handleSubmit(onSubmit)();
                      }
                    }}
                    length={6}
                    TextFieldsProps={{
                      placeholder: '#',
                      inputMode: 'numeric',
                      type: 'tel',
                    }}
                    validateChar={(value) => {
                      return value.trim() !== '' && !isNaN(Number(value));
                    }}
                  />
                )}
              />
            </div>
            <div className={styles.buttonContainer}>
              <div className={styles.buttonWrapper}>
                <Button type="submit" className={styles.formBtn} disabled={loading}>
                  Submit
                </Button>
              </div>
            </div>
            <div
              className={styles.accessToEmail}
              onClick={() => setContactCustomerSupport(true)}
              style={{ opacity: 0.7 }}
            >
              I no longer have access to this phone number
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

Modal.setAppElement('#root');

export default UpdateEmailModalStepTwo;
